import { Box, Stack, Typography } from '@mui/material';
import {
  TRACKER_EYEBROW_SX,
  TRACKER_HAIRLINE_SX,
  TRACKER_STAT_CARD_SX,
} from './guestTableStyles';
import type { TrackerSummary } from '../../../services/tracker-api-client';

interface StatEntry {
  key: string;
  label: string;
  value: number;
  /** The one number in the group that carries the good news. */
  tone?: 'brand';
}

/**
 * Three labelled stat groups, computed server-side from the same rows the table
 * renders. Grouped rather than a row of tiles: "42 going" only means something
 * next to the other RSVP states.
 *
 * Each group is its own small bordered card — value first, then its word, on
 * one baseline, so the whole roster reads as a sentence at a glance.
 */
function StatGroup({
  label,
  entries,
  emptyLabel,
}: {
  label: string;
  entries: StatEntry[];
  emptyLabel: string;
}) {
  return (
    <Box
      component="section"
      aria-label={label}
      sx={{
        ...TRACKER_STAT_CARD_SX,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 1.75,
        rowGap: 1,
      }}
    >
      <Typography component="h3" sx={{ ...TRACKER_EYEBROW_SX, color: 'var(--tsb-fg-muted)' }}>
        {label}
      </Typography>
      <Box aria-hidden="true" sx={{ ...TRACKER_HAIRLINE_SX, display: { xs: 'none', sm: 'block' } }} />
      {entries.length ? (
        <Stack
          component="dl"
          direction="row"
          useFlexGap
          flexWrap="wrap"
          alignItems="baseline"
          sx={{ m: 0, columnGap: 1.75, rowGap: 0.75, minWidth: 0 }}
        >
          {entries.map((entry) => (
            // A <dl> wants dt before dd; the number reads first, so the pair is
            // ordered visually rather than by rewriting the markup.
            <Box
              key={entry.key}
              sx={{ display: 'inline-flex', alignItems: 'baseline', columnGap: 0.625, minWidth: 0 }}
            >
              <Typography
                component="dt"
                sx={{
                  order: 2,
                  color: 'var(--tsb-fg-muted)',
                  fontSize: 12,
                  lineHeight: 1.35,
                  overflowWrap: 'anywhere',
                }}
              >
                {entry.label}
              </Typography>
              <Typography
                component="dd"
                sx={{
                  order: 1,
                  m: 0,
                  color: entry.tone === 'brand' ? 'var(--tsb-brand-ink)' : 'var(--tsb-fg)',
                  fontSize: 14,
                  fontWeight: 700,
                  lineHeight: 1.35,
                }}
              >
                {entry.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography sx={{ color: 'var(--tsb-fg-muted)', fontSize: 12 }}>{emptyLabel}</Typography>
      )}
    </Box>
  );
}

export function GuestSummary({ summary }: { summary: TrackerSummary }) {
  const rsvps = summary.rsvps;
  const bookedVia = summary.bookedVia;
  // Long tails belong in the table, not the header.
  const hotels = summary.byHotel.slice(0, 4);

  return (
    <Box>
      <Stack direction="row" useFlexGap flexWrap="wrap" sx={{ gap: 1.5 }}>
        <StatGroup
          label="RSVPs"
          emptyLabel="No guests yet."
          entries={[
            { key: 'going', label: 'going', value: rsvps.going, tone: 'brand' },
            { key: 'maybe', label: 'maybe', value: rsvps.maybe },
            { key: 'cant_go', label: "can't go", value: rsvps.cantGo },
            // The roster's own vocabulary, so a number here names the same
            // state as the pill in the row it came from.
            { key: 'invited', label: 'invited', value: rsvps.invited },
            { key: 'total', label: 'on the list', value: rsvps.total },
          ]}
        />
        <StatGroup
          label="By hotel"
          emptyLabel="Nobody has a hotel yet."
          entries={hotels.map((hotel) => ({
            key: hotel.stayingAt,
            label: hotel.stayingAt,
            value: hotel.count,
          }))}
        />
        <StatGroup
          label="Booked via"
          emptyLabel="Nothing booked yet."
          entries={[
            { key: 'tripsby', label: 'Tripsby', value: bookedVia.tripsby, tone: 'brand' },
            { key: 'guest', label: 'by guest', value: bookedVia.guest },
            { key: 'unknown', label: 'not set', value: bookedVia.unknown },
          ]}
        />
      </Stack>
      {summary.byHotel.length > hotels.length ? (
        <Typography sx={{ mt: 1.25, color: 'var(--tsb-fg-muted)', fontSize: 12 }}>
          {`+ ${summary.byHotel.length - hotels.length} more ${
            summary.byHotel.length - hotels.length === 1 ? 'hotel' : 'hotels'
          } in the table below.`}
        </Typography>
      ) : null}
    </Box>
  );
}

export default GuestSummary;
